import React from 'react';
import AISuggestOffFormTextarea from '../AISuggestOffFormTextarea';
import AISuggestOffForm from '../AISuggestOffForm';
import BCPGuideSection from '../BCPGuideSection';
import type { BCPFieldConfig, ChecklistFieldConfig, TableFieldConfig, TextareaFieldConfig } from '../../config/bcpFieldConfig';

interface BCPFieldRendererProps {
  field: BCPFieldConfig;
  values: Record<string, string>;
  errors?: Record<string, string>;
  onChange: (fieldId: string, value: string) => void;
  onAIConsult?: (fieldId: string) => void;
}

const TEXTAREA_HEIGHT_MAP: Record<Exclude<TextareaFieldConfig['minHeight'], undefined>, string> = {
  small: '72px',
  medium: '96px',
  large: '300px',
};

const getTextareaHeight = (field: TextareaFieldConfig): string => {
  if (!field.minHeight) {
    return TEXTAREA_HEIGHT_MAP.medium;
  }
  return TEXTAREA_HEIGHT_MAP[field.minHeight] ?? TEXTAREA_HEIGHT_MAP.medium;
};

const renderGuideSection = (field: BCPFieldConfig) => {
  if (!field.guide) {
    return null;
  }

  const { content, example } = field.guide;

  if (content && example) {
    return (
      <div style={{ marginTop: '8px' }}>
        <BCPGuideSection guideContent={content} exampleContent={example} />
      </div>
    );
  }

  if (content || example) {
    return (
      <div style={{ marginTop: '8px' }}>
        <BCPGuideSection>
          {content ?? example}
        </BCPGuideSection>
      </div>
    );
  }

  return null;
};

const renderTextareaField = (
  field: TextareaFieldConfig,
  value: string,
  onChange: (fieldId: string, value: string) => void,
  onAIConsult?: (fieldId: string) => void
) => {
  // placeholderにdefaultValueを優先的に使用
  const placeholder = field.placeholder ?? field.defaultValue;

  return (
    <AISuggestOffFormTextarea
      label={field.label ?? ''}
      value={value}
      onChange={(nextValue) => onChange(field.id, nextValue)}
      placeholder={placeholder}
      showAIButton={field.aiConsult}
      minHeight={getTextareaHeight(field)}
      onAIConsult={onAIConsult ? () => onAIConsult(field.id) : undefined}
      highlightPlaceholders={field.highlightPlaceholders}
    />
  );
};

const renderInputField = (
  field: BCPFieldConfig,
  value: string,
  onChange: (fieldId: string, value: string) => void,
  onAIConsult?: (fieldId: string) => void
) => {
  return (
    <AISuggestOffForm
      label={field.label ?? ''}
      value={value}
      onChange={(nextValue) => onChange(field.id, nextValue)}
      placeholder={field.placeholder}
      showAIButton={field.aiConsult}
      onAIConsult={onAIConsult ? () => onAIConsult(field.id) : undefined}
    />
  );
};

const renderChecklistField = (
  field: ChecklistFieldConfig,
  values: Record<string, string>,
  onChange: (fieldId: string, value: string) => void
) => {
  return (
    <div className="space-y-2">
      {field.checklistItems.map((item, index) => {
        const key = `${field.id}_${index}`;
        const checked = values[key] === 'true';
        return (
          <label key={key} className="flex items-center">
            <input
              type="checkbox"
              checked={checked}
              onChange={(event) => onChange(key, event.target.checked.toString())}
              className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">{item}</span>
          </label>
        );
      })}
    </div>
  );
};

const renderTableField = (
  field: TableFieldConfig,
  values: Record<string, string>,
  onChange: (fieldId: string, value: string) => void
) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        {field.table.headers && (
          <thead className="bg-gray-50">
            <tr>
              {field.table.headers.map((header, index) => (
                <th
                  key={index}
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody className="bg-white divide-y divide-gray-200">
          {field.table.rows.map((row, rowIndex) => {
            const columns = row.columnPlaceholders?.length ?? field.table.headers?.length ?? 0;
            return (
              <tr key={`${field.id}_${rowIndex}`}>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {row.label}
                </td>
                {Array.from({ length: columns }).map((_, columnIndex) => {
                  const key = `${field.id}_${rowIndex}_${columnIndex}`;
                  return (
                    <td key={key} className="px-4 py-2">
                      <input
                        type="text"
                        value={values[key] ?? ''}
                        onChange={(event) => onChange(key, event.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder={row.columnPlaceholders?.[columnIndex]}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const BCPFieldRenderer: React.FC<BCPFieldRendererProps> = ({ field, values, errors, onChange, onAIConsult }) => {
  // フォームの値を取得(空文字列も含む)
  const value = values[field.id] ?? '';
  const errorMessage = field.type !== 'table' && field.type !== 'checklist' ? errors?.[field.id] : undefined;

  return (
    <div>
      {(() => {
        switch (field.type) {
          case 'textarea':
            return renderTextareaField(field, value, onChange, onAIConsult);
          case 'input':
            return renderInputField(field, value, onChange, onAIConsult);
          case 'checklist':
            return renderChecklistField(field, values, onChange);
          case 'table':
            return renderTableField(field, values, onChange);
          case 'static':
            return field.content;
          default:
            return null;
        }
      })()}

      {errorMessage && (
        <p className="text-sm text-red-600 ml-6">{errorMessage}</p>
      )}

      {field.note && (
        <div className="mt-2 bg-gray-50 p-3 rounded-md">
          <p className="text-xs text-gray-600 whitespace-pre-line">{field.note}</p>
        </div>
      )}

      {renderGuideSection(field)}
    </div>
  );
};

export default BCPFieldRenderer;
